import $ from 'jquery';

/**
 * @desc Template of the element which eventually appends to DOM to determine current scroll type.
 * @type {string}
 */
export const defaultTestItem = `
    <div dir="rtl" style="font-size: 14px; width: 4px; height: 1px; position: absolute; top: -1000px; overflow: scroll">
        ....
    </div>
`;

/**
 * @desc Horizontal scrollbar with rtl(right to left) language support have different implement in different browsers.
 * scrollLeft in rtl element is not defined by any spec or standards. So different browser have different implement.
 * As far as I know, there are 3 implements: WebKit, Firefox/Opera, IE.
 * WebKit's implement is the most easy to use implement. Exactly the same as ltr(left to right) element.
 * Except the initial position of scrollbar controller is at most right position.
 * Firefox has a clearly define in their mdn document. The most right position stands for 0.
 * And when user scrolls to left. The value decreases. The value is possible to be negative in this implement.
 * IE thought the element is flip horizontal. So the most right position is 0. And if it scrolls to left.
 * The value increase.
 * @example3 Types of scrollLeft (scrollWidth = 100):
 * Browser       | Type     | Most Left | Most Right | Initial
 * WebKit        | default  | 0         | 100        | 100
 * Firefox/Opera | negative | -100      | 0          | 0
 * IE            | reverse  | 100       | 0          | 0
 * @see {@link https://github.com/othree/jquery.rtl-scroll-type}
 */
export default class UniScroll {
  constructor({ testItem = defaultTestItem } = {}) {
    this.SCROLL_DEFAULT = 'default';
    this.SCROLL_REVERSE = 'reverse';
    this.SCROLL_NEGATIVE = 'negative';

    this.testItem = testItem;

    this.detectScrollType();
  }

  /**
   * @param {HTMLElement} element
   * @param {number} offset
   * @return {number}
   */
  static defaultOffsetToNegative(element, offset) {
    return -Math.abs(element.scrollWidth - (offset + $(element).width()));
  }

  /**
   * @param {HTMLElement} element
   * @param {number} offset
   * @return {number}
   */
  static defaultOffsetToReverse(element, offset) {
    return Math.abs(element.scrollWidth - (offset + $(element).width()));
  }

  /**
   * @modifies {rtlScrollType}
   * @return {string}
   */
  detectScrollType() {
    const testItem = $(this.testItem).appendTo('body')[0];

    // Initial scroll type like in IE
    let scrollType = this.SCROLL_REVERSE;

    // Only Chrome has initial scrollLeft equals to > 0
    if (testItem.scrollLeft > 0) {
      scrollType = this.SCROLL_DEFAULT;
    } else {
      testItem.scrollLeft = 1;

      // If scrollLeft rolled back to 0, then it's Firefox/Opera
      if (testItem.scrollLeft === 0) {
        scrollType = this.SCROLL_NEGATIVE;
      }
    }

    $(testItem).remove();

    this.rtlScrollType = scrollType;

    return scrollType;
  }

  /**
   * @param {HTMLElement} element
   * @return {number}
   */
  scrollLeft(element) {
    switch (this.rtlScrollType) {
      case this.SCROLL_NEGATIVE:
        return element.scrollLeft + element.scrollWidth - $(element).width();
      case this.SCROLL_REVERSE:
        return element.scrollWidth - element.scrollLeft - $(element).width();
      default:
        return element.scrollLeft;
    }
  }

  /**
   * @desc Converts Webkit-like coordinates to the current scroll-type coordinates.
   * @param {HTMLElement} element
   * @param {number} offset
   * @return {number}
   */
  getNormalizedOffset(element, offset) {
    switch (this.rtlScrollType) {
      case this.SCROLL_NEGATIVE:
        if ((offset + $(element).width() >= element.scrollWidth)) {
          return 0;
        }

        return UniScroll.defaultOffsetToNegative(element, offset);
      case this.SCROLL_REVERSE:
        if ((offset + $(element).width() >= element.scrollWidth)) {
          return 0;
        }

        return UniScroll.defaultOffsetToReverse(element, offset);
      default:
        return offset;
    }
  }
}
